import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MdAdd, MdArrowBack, MdEdit, MdPerson, MdVerifiedUser } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppCard,
  FileViewerModal,
  OutlineButton,
  PrimaryButton,
  SortableTh,
  StatusAlertCard,
  StatusBadge,
  PageLoader,
} from '../../../../components/ui';
import { useTableSort } from '../../../../hooks/useTableSort';
import { formatGender } from '../../../../data/master/people';
import { listEquipment } from '../../../../repositories/equipment.repository';
import { listDocuments } from '../../../../repositories/document.repository';
import { getVillager } from '../../../../repositories/villager.repository';
import { profilePhotoFromDocuments } from '../../../../models';
import { ageFromDob, formatDobDisplay } from '../../../../utils/personName';
import { VerificationStatusModal } from './VerificationStatusModal';
import { VillagerDocumentsSection } from './VillagerDocumentsSection';
import { VillagerFormModal } from './VillagerFormModal';
import { VendorAssetFormModal } from '../vendors/VendorAssetFormModal';

const DETAIL_COLUMNS = { base: 1, sm: 2, md: 3, lg: 4, xl: 5, '2xl': 6 };

function DetailRow({ label, value }) {
  return (
    <Box
      p={{ base: 3, xl: 4 }}
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
      w="100%"
    >
      <Text
        fontSize={{ base: '12px', xl: '13px' }}
        color="text.secondary"
        mb={1}
        fontWeight="500"
      >
        {label}
      </Text>
      <Text
        fontSize={{ base: '15px', xl: '16px' }}
        fontWeight="600"
        whiteSpace="pre-wrap"
        lineHeight="1.4"
      >
        {value || '—'}
      </Text>
    </Box>
  );
}

export default function VillagerDetailPage() {
  const { peopleId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const editModal = useDisclosure();
  const statusModal = useDisclosure();
  const assetModal = useDisclosure();
  const fileViewer = useDisclosure();
  const [viewerFile, setViewerFile] = useState(null);
  const [person, setPerson] = useState(null);
  const [assets, setAssets] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [profileDoc, setProfileDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyDocuments = useCallback((docList, photoDocumentId) => {
    const list = Array.isArray(docList) ? docList : [];
    setDocuments(list);
    const profile = profilePhotoFromDocuments(list, photoDocumentId);
    setProfileDoc(profile);
    setPerson((p) => {
      if (!p) return p;
      if (!profile) return { ...p, photoDocumentId: photoDocumentId ?? p.photoDocumentId };
      return {
        ...p,
        photoDocumentId: profile.id,
        photo: { name: profile.name, url: profile.url },
      };
    });
  }, []);

  const reloadEquipment = useCallback(async () => {
    if (!person?.id) return;
    const assetRes = await listEquipment({
      pageSize: 200,
      ownerType: 'villager',
      villagerId: person.id,
    });
    setAssets(assetRes.items || []);
  }, [person?.id]);

  const reloadDocuments = useCallback(
    async (photoDocumentId) => {
      if (!person?.id) return;
      const docs = await listDocuments({
        ownerType: 'villager',
        ownerId: person.id,
      }).catch(() => []);
      applyDocuments(docs, photoDocumentId ?? person.photoDocumentId);
    },
    [applyDocuments, person?.id, person?.photoDocumentId],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const loaded = await getVillager(peopleId);
      setPerson(loaded);
      if (loaded?.id) {
        const [assetRes, docs] = await Promise.all([
          listEquipment({
            pageSize: 200,
            ownerType: 'villager',
            villagerId: loaded.id,
          }),
          listDocuments({ ownerType: 'villager', ownerId: loaded.id }).catch(() => []),
        ]);
        setAssets(assetRes.items || []);
        const docList = Array.isArray(docs) ? docs : [];
        const profile = profilePhotoFromDocuments(docList, loaded.photoDocumentId);
        setDocuments(docList);
        setProfileDoc(profile);
        if (profile) {
          setPerson({
            ...loaded,
            photoDocumentId: profile.id,
            photo: { name: profile.name, url: profile.url },
          });
        }
      } else {
        setAssets([]);
        setDocuments([]);
        setProfileDoc(null);
      }
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load villager',
        status: 'error',
        position: 'top-right',
      });
      setPerson(null);
      setProfileDoc(null);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [peopleId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const {
    sorted: sortedAssets,
    sort,
    toggleSort,
  } = useTableSort(assets, {
    makeModel: (a) => [a.manufacturer, a.model].filter(Boolean).join(' '),
  });
  const age = person ? ageFromDob(person.dob) : null;
  const verificationStatus = person?.verificationStatus || 'Pending';
  const verificationBadge =
    verificationStatus === 'Verified'
      ? 'verified'
      : verificationStatus === 'Rejected'
        ? 'error'
        : 'pending';

  if (loading) {
    return <PageLoader label="Loading villager…" />;
  }

  if (!person) {
    return (
      <Box>
        <OutlineButton
          size="sm"
          leftIcon={<MdArrowBack />}
          mb={4}
          onClick={() => navigate('/admin/equipments/villagers')}
        >
          Back to villagers
        </OutlineButton>
        <AppCard py={12} textAlign="center">
          <Text fontWeight="600" fontSize={{ base: '16px', xl: '18px' }}>
            Villager not found
          </Text>
          <Text mt={2} color="text.secondary" fontSize={{ base: '14px', xl: '15px' }}>
            No People Master record for {peopleId}.
          </Text>
        </AppCard>
      </Box>
    );
  }

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        <Box>
          <OutlineButton
            size="sm"
            leftIcon={<MdArrowBack />}
            mb={3}
            onClick={() => navigate('/admin/equipments/villagers')}
          >
            Back
          </OutlineButton>
          <Flex align="center" gap={{ base: 3, md: 4 }}>
            <Box
              as="button"
              type="button"
              flexShrink={0}
              onClick={() => {
                if (!profileDoc?.url) return;
                setViewerFile(profileDoc);
                fileViewer.onOpen();
              }}
              cursor={profileDoc?.url ? 'pointer' : 'default'}
              borderRadius="md"
              overflow="hidden"
              borderWidth="1px"
              borderColor="border.subtle"
              bg="bg.input"
              w={{ base: '64px', md: '80px' }}
              h={{ base: '64px', md: '80px' }}
              _hover={profileDoc?.url ? { borderColor: 'utkarsh.amber' } : undefined}
              aria-label="View profile photo"
            >
              {profileDoc?.url ? (
                <Image
                  src={profileDoc.url}
                  alt={person.fullName || 'Profile'}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                />
              ) : (
                <Flex w="100%" h="100%" align="center" justify="center">
                  <Icon as={MdPerson} boxSize={7} color="text.secondary" />
                </Flex>
              )}
            </Box>
            <Box minW={0}>
              <Heading size={{ base: 'lg', xl: 'xl' }} noOfLines={2}>
                {person.fullName}
              </Heading>
              <Flex align="center" gap={3} mt={2} wrap="wrap">
                <Text
                  color="text.secondary"
                  fontFamily="mono"
                  fontSize={{ base: '14px', xl: '15px' }}
                >
                  {person.peopleId}
                </Text>
                <StatusBadge status={verificationBadge}>{verificationStatus}</StatusBadge>
              </Flex>
            </Box>
          </Flex>
        </Box>
        <HStack spacing={2} align="start">
          <OutlineButton
            size="sm"
            leftIcon={<MdVerifiedUser />}
            onClick={statusModal.onOpen}
          >
            Update status
          </OutlineButton>
          <PrimaryButton size="sm" leftIcon={<MdEdit />} onClick={editModal.onOpen}>
            Edit
          </PrimaryButton>
        </HStack>
      </Flex>

      {verificationStatus === 'Rejected' && person.rejectionReason ? (
        <Box mb={6}>
          <StatusAlertCard
            variant="error"
            title="Rejected"
            body={person.rejectionReason}
            footer="Update status to change verification or clear this reason."
            icon={MdVerifiedUser}
          />
        </Box>
      ) : null}

      {verificationStatus === 'Pending' ? (
        <Box mb={6}>
          <StatusAlertCard
            title="Verification pending"
            body="This villager is awaiting review. Use Update status to Verify or Reject."
            icon={MdVerifiedUser}
          />
        </Box>
      ) : null}

      <AppCard mb={6}>
        <Heading size={{ base: 'sm', xl: 'md' }} mb={4}>
          Personal details
        </Heading>
        <SimpleGrid columns={DETAIL_COLUMNS} spacing={{ base: 3, xl: 4 }}>
          <DetailRow label="First Name" value={person.name} />
          <DetailRow label="Father’s / Husband’s Name" value={person.fatherHusbandName} />
          <DetailRow label="Surname" value={person.surname} />
          <DetailRow label="Full Name" value={person.fullName} />
          <DetailRow label="Mobile" value={person.mobile} />
          <DetailRow label="Email" value={person.email} />
          <DetailRow label="Aadhaar" value={person.aadhaar} />
          <DetailRow label="PAN" value={person.pan} />
          <DetailRow label="Election Card" value={person.electionCard} />
          <DetailRow label="Driving License" value={person.drivingLicense} />
          <DetailRow label="Date of Birth" value={formatDobDisplay(person.dob)} />
          <DetailRow label="Age" value={age != null ? String(age) : '—'} />
          <DetailRow label="Gender" value={formatGender(person.gender)} />
          <DetailRow label="Education" value={person.education} />
          <DetailRow label="Education Details" value={person.educationDetails} />
          <DetailRow label="Verification" value={verificationStatus} />
        </SimpleGrid>
      </AppCard>

      <AppCard mb={6}>
        <Heading size={{ base: 'sm', xl: 'md' }} mb={4}>
          Address
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 3, xl: 4 }}>
          <DetailRow label="Address 1" value={person.address1} />
          <DetailRow label="Address 2" value={person.address2} />
          <DetailRow label="Village / Town / City" value={person.villageName} />
          <DetailRow label="PIN" value={person.pin || person.villagePin} />
        </SimpleGrid>
      </AppCard>

      <VillagerDocumentsSection
        person={person}
        documents={documents}
        onOpen={(doc) => {
          if (!doc?.url) return;
          setViewerFile(doc);
          fileViewer.onOpen();
        }}
        onChanged={reloadDocuments}
      />

      <AppCard p={0} overflow="hidden">
        <Box px={{ base: 5, xl: 6 }} pt={{ base: 5, xl: 6 }} pb={3}>
          <Flex justify="space-between" align="start" gap={3} wrap="wrap">
            <Box>
              <Heading size={{ base: 'sm', xl: 'md' }}>Equipment</Heading>
              <Text fontSize={{ base: '13px', xl: '14px' }} color="text.secondary" mt={1}>
                Assets registered under this villager. Click a row for full equipment details.
              </Text>
            </Box>
            <OutlineButton size="sm" leftIcon={<MdAdd />} onClick={assetModal.onOpen}>
              Add equipment
            </OutlineButton>
          </Flex>
        </Box>
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead bg="bg.input">
              <Tr>
                {[
                  { key: 'assetId', label: 'Asset ID' },
                  { key: 'equipmentDescription', label: 'Description' },
                  { key: 'equipmentType', label: 'Type' },
                  { key: 'vehicleClass', label: 'Class' },
                  { key: 'makeModel', label: 'Make / Model' },
                  { key: 'regNo', label: 'Reg. No.' },
                  { key: 'fuelType', label: 'Fuel' },
                  { key: 'status', label: 'Verification' },
                  { key: 'operationalStatus', label: 'Status' },
                ].map((col) => (
                  <SortableTh
                    key={col.key}
                    columnKey={col.key}
                    sort={sort}
                    onSort={toggleSort}
                    fontSize={{ base: '12px', xl: '13px' }}
                  >
                    {col.label}
                  </SortableTh>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {sortedAssets.map((a) => (
                <Tr
                  key={a.id || a.assetId}
                  cursor="pointer"
                  _hover={{ bg: 'bg.input' }}
                  onClick={() =>
                    navigate(
                      `/admin/equipments/villagers/${person.peopleId || peopleId}/assets/${a.assetId}`,
                    )
                  }
                >
                  <Td fontFamily="mono" fontSize={{ base: '13px', xl: '14px' }}>
                    {a.assetId}
                  </Td>
                  <Td fontWeight="600" fontSize={{ base: '14px', xl: '15px' }}>
                    {a.equipmentDescription || a.equipmentType}
                  </Td>
                  <Td fontSize={{ base: '14px', xl: '15px' }} color="text.secondary">
                    {a.equipmentType}
                  </Td>
                  <Td fontSize={{ base: '14px', xl: '15px' }}>{a.vehicleClass}</Td>
                  <Td fontSize={{ base: '14px', xl: '15px' }}>
                    {a.manufacturer} {a.model}
                  </Td>
                  <Td fontFamily="mono" fontSize={{ base: '13px', xl: '14px' }}>
                    {a.regNo}
                  </Td>
                  <Td fontSize={{ base: '14px', xl: '15px' }}>{a.fuel}</Td>
                  <Td>
                    <StatusBadge
                      status={a.status === 'Verified' ? 'verified' : 'pending'}
                    >
                      {a.status}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <StatusBadge
                      status={
                        a.operationalStatus === 'Deployed' ? 'warning' : 'verified'
                      }
                    >
                      {a.operationalStatus || 'Available'}
                    </StatusBadge>
                  </Td>
                </Tr>
              ))}
              {assets.length === 0 ? (
                <Tr>
                  <Td colSpan={9}>
                    <Text
                      color="text.secondary"
                      py={8}
                      textAlign="center"
                      fontSize={{ base: '14px', xl: '15px' }}
                    >
                      No equipment registered for this villager yet. Use Add equipment to register
                      a machine.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </Box>
      </AppCard>

      <VerificationStatusModal
        isOpen={statusModal.isOpen}
        onClose={statusModal.onClose}
        person={person}
        onSaved={load}
      />

      <VillagerFormModal
        isOpen={editModal.isOpen}
        onClose={editModal.onClose}
        person={person}
        onSaved={load}
      />

      <VendorAssetFormModal
        isOpen={assetModal.isOpen}
        onClose={assetModal.onClose}
        person={person}
        onSaved={reloadEquipment}
      />

      <FileViewerModal
        isOpen={fileViewer.isOpen}
        onClose={() => {
          fileViewer.onClose();
          setViewerFile(null);
        }}
        file={viewerFile}
      />
    </Box>
  );
}
